/**
 * URL validation utility to prevent XSS attacks via dangerous protocols.
 * Only allows safe protocols: http, https, ftp.
 * Blocks: javascript:, data:, vbscript:, file:, etc.
 */

const DANGEROUS_PROTOCOLS = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'about:',
  'chrome:',
  'chrome-extension:',
  'moz-extension:',
];

/**
 * Validates a URL to ensure it doesn't contain dangerous protocols
 * @param {string} url - URL string to validate
 * @returns {string} - Validated URL
 * @throws {Error} - If URL contains a dangerous protocol
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return '';
  }

  const urlLower = trimmedUrl.toLowerCase();

  // Check for dangerous protocols (case-insensitive)
  for (const protocol of DANGEROUS_PROTOCOLS) {
    if (urlLower.startsWith(protocol)) {
      throw new Error(
        `Dangerous protocol '${protocol}' is not allowed. ` +
        `Only http://, https://, and ftp:// URLs are permitted.`
      );
    }
  }

  // Allow safe protocols: http, https, ftp
  // Also allow relative URLs (starting with /) and protocol-relative URLs (starting with //)
  if (
    urlLower.startsWith('http://') ||
    urlLower.startsWith('https://') ||
    urlLower.startsWith('ftp://') ||
    trimmedUrl.startsWith('/') ||
    trimmedUrl.startsWith('//')
  ) {
    return trimmedUrl;
  }

  // If it doesn't start with a protocol, assume it's a relative URL or invalid
  // For safety, require explicit protocol
  if (!urlLower.startsWith('http://') && !urlLower.startsWith('https://') && !urlLower.startsWith('ftp://')) {
    // If it looks like it might be a domain (contains a dot), suggest https://
    if (trimmedUrl.includes('.') && !trimmedUrl.startsWith('/')) {
      throw new Error(
        `URL must start with http://, https://, or ftp://. ` +
        `Relative URLs starting with / are also allowed.`
      );
    }
    // Otherwise, treat as relative URL (starting with /)
    if (!trimmedUrl.startsWith('/')) {
      throw new Error(
        `Invalid URL format. Must start with http://, https://, ftp://, or /`
      );
    }
  }

  return trimmedUrl;
};

/**
 * Safely opens a URL in a new window/tab after validation
 * @param {string} url - URL to open
 * @param {string} target - Window target (default: '_blank')
 * @returns {Window|null} - The opened window, or null if validation failed
 */
export const safeOpenUrl = (url, target = '_blank') => {
  try {
    const validatedUrl = validateUrl(url);
    if (validatedUrl) {
      return window.open(validatedUrl, target);
    }
    return null;
  } catch (error) {
    console.error('Invalid URL:', error.message);
    // Optionally show user-friendly error message
    alert(`Invalid URL: ${error.message}`);
    return null;
  }
};

